import styled, { css } from 'styled-components/macro';

import { ProgressBarMode, ValuePosition } from './helpers';

import { hexToRGB } from '@repo-level/core-front';
import { PROGRESS_WIDTH_VALUE } from '@repo-level/core-profile/components/LinearProgressBar/helpers';

export interface ScProgressBarProps {
  value: number;
  mode: ProgressBarMode;
  height?: number;
  padding?: number;
  valuePosition?: ValuePosition;
  borderRadius?: string;
  borderRadiusInner?: string;
  withDots?: boolean;
  progressMinWidth?: string;
}

export const ScProgressBar = styled.div<ScProgressBarProps>`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${({ mode }) =>
    [ProgressBarMode.GREEN, ProgressBarMode.BLUE].includes(mode)
      ? '4px'
      : '8px'};
  width: 100%;

  ${({ valuePosition }) =>
    valuePosition === ValuePosition.BOTTOM &&
    css`
      flex-direction: column;
    `};

  ${({ mode, theme }) =>
    [ProgressBarMode.ERROR, ProgressBarMode.SUCCESS].includes(mode) &&
    css`
      border-radius: 8px;
      background: ${theme.blackColor};
    `};

  .progress-bar-line {
    display: flex;
    position: relative;
    width: 100%;
    padding: ${({ padding }) => `${padding || 0}px`};
    ${({ mode }) =>
      [ProgressBarMode.GREEN, ProgressBarMode.BLUE].includes(mode) &&
      css`
        padding: 1px;
      `};
    height: ${({ height }) => `${height || 20}px`};
    border-radius: ${({ borderRadius }) => borderRadius || '6px'};
    background-color: ${({ theme, mode }) => {
      switch (mode) {
        case ProgressBarMode.PRIMARY:
          return theme.grayColor5;
        case ProgressBarMode.PRIMARY_DARK:
          return theme.grayColor4;
        case ProgressBarMode.GREEN:
          return hexToRGB(theme.whiteColor, 0.2);
        case ProgressBarMode.BLUE:
          return hexToRGB(theme.whiteColor, 0.2);
        default:
          return theme.grayColor4t;
      }
    }};

    & > .completed {
      position: ${({ mode }) =>
        [ProgressBarMode.GREEN, ProgressBarMode.BLUE].includes(mode)
          ? 'static'
          : 'absolute'};
      left: 0;
      top: -2px;
      height: ${({ height }) => `${height || 24}px`};
      ${({ mode }) =>
        [ProgressBarMode.GREEN, ProgressBarMode.BLUE].includes(mode) &&
        css`
          height: 100%;
        `};
    }
    width: ${({ value, progressMinWidth }) =>
      PROGRESS_WIDTH_VALUE.includes(value) && progressMinWidth
        ? progressMinWidth
        : `${value}%`};
    background: ${({ theme, mode }) => {
      switch (mode) {
        case ProgressBarMode.GREEN:
          return theme.springWaveGradient;
        case ProgressBarMode.BLUE:
          return theme.oceanBreezeGradient;
        case ProgressBarMode.ERROR:
          return theme.pinkRougeGradient;
        case ProgressBarMode.SUCCESS:
          return theme.greenMarineGradient;
        default:
          return theme.lavenderBlueGradient;
      }
    }};
    transition: width 0.5s ease-in-out;
    border-radius: ${({ borderRadiusInner, withDots }) =>
      withDots ? '360px' : borderRadiusInner || '8px'};
  }

  .current-progress {
    white-space: nowrap;
    font-weight: 700;
    font-size: ${({ mode }) =>
      [ProgressBarMode.GREEN, ProgressBarMode.BLUE].includes(mode)
        ? '12px'
        : '14px'};
    line-height: ${({ mode }) =>
      [ProgressBarMode.GREEN, ProgressBarMode.BLUE].includes(mode)
        ? '18px'
        : '20px'};
    color: ${({ theme }) => theme.whiteColor};

    ${({ valuePosition, value }) => {
      if (valuePosition === ValuePosition.BOTTOM) return;
      return valuePosition === ValuePosition.TOP
        ? css`
            position: absolute;
            left: ${value}%;
            transform: translate(-50%, -120%);
            text-align: center;
          `
        : valuePosition === ValuePosition.INSIDE && value > 20
        ? css`
            text-align: right;
            padding-right: 8px;
            padding-top: 2px;
          `
        : css`
            position: absolute;
            left: 100%;
            padding-left: 4px;
            padding-top: 2px;
          `;
    }}
  }

  .dots {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: absolute;
    top: 4px;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    height: 8px;
    padding: 0 2px 0 2px;
    z-index: 10;
  }

  .dot {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.grayColor7};
  }

  .dot.active {
    background-color: ${({ theme }) => theme.whiteColor};
  }
`;
